import React, { useState, useEffect } from 'react';
import { 
  Search, IndianRupee, Clock, AlertTriangle, 
  TrendingUp, CalendarDays, CalendarMinus, 
  ChevronLeft, ChevronRight, UserCircle2, MapPin
} from 'lucide-react';

function PendingBillsList() {
  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Fetch Bills on mount
  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    try {
      const response = await fetch('https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/pending-bills');
      const result = await response.json();
      if (result.success) {
        setBills(result.data);
      } else {
        setError('Failed to fetch bills.');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Server connection failed.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Status Change directly from the table dropdown
  const handleStatusChange = async (billId, newStatus) => {
    // 1. Optimistically update UI for instant feedback
    const originalBills = [...bills];
    setBills(bills.map(b => b._id === billId ? { ...b, paymentStatus: newStatus } : b));

    // 2. Send update to backend
    try {
      const response = await fetch(`https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/pending-bills/${billId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentStatus: newStatus })
      });
      const data = await response.json();
      
      if (!data.success) {
        // Revert if backend fails
        setBills(originalBills);
        alert('Failed to update status on server.');
      }
    } catch (error) {
      setBills(originalBills);
      alert('Network error while updating status.');
    }
  };

  // --- CALCULATIONS (Only counts bills that are NOT 'Completed') ---
  const activeBills = bills.filter(b => b.paymentStatus !== 'Completed');

  // 1. Total Pending Amount
  const totalPendingAmount = activeBills.reduce((sum, bill) => sum + (Number(bill.pendingAmount) || 0), 0);

  // Date Logic for This Month / Last Month
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

  // 2. This Month Pending
  const thisMonthPending = activeBills.filter(b => {
    const d = new Date(b.createdAt);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  }).reduce((sum, b) => sum + (Number(b.pendingAmount) || 0), 0);

  // 3. Last Month Pending
  const lastMonthPending = activeBills.filter(b => {
    const d = new Date(b.createdAt);
    return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
  }).reduce((sum, b) => sum + (Number(b.pendingAmount) || 0), 0);

  // 4. Executive Summaries (Group by Executive)
  const executiveStats = activeBills.reduce((acc, bill) => {
    const execName = bill.salesExecutiveName?.trim() || 'Unassigned';
    if (!acc[execName]) acc[execName] = { count: 0, amount: 0 };
    acc[execName].count += 1;
    acc[execName].amount += Number(bill.pendingAmount) || 0;
    return acc;
  }, {});

  // --- PAGINATION LOGIC ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTableBills = bills.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(bills.length / itemsPerPage);

  return (
    <div className="min-h-screen bg-[#0B0F19] p-6 lg:p-8 text-slate-300">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header */}
        <div>
          <h1 className="text-3xl font-extrabold text-white flex items-center gap-3 tracking-wide">
            <Clock className="text-indigo-500" size={32} />
            Pending Bills Dashboard
          </h1>
          <p className="text-slate-400 mt-2 text-sm">Real-time overview of outstanding collections.</p>
        </div>

        {/* --- SUMMARY CARDS (Top) --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total Pending */}
          <div className="bg-gradient-to-br from-[#111624] to-[#1a2133] p-6 rounded-2xl border border-white/5 shadow-xl relative overflow-hidden group">
            <div className="absolute -right-6 -top-6 w-24 h-24 bg-indigo-500/10 rounded-full blur-2xl group-hover:bg-indigo-500/20 transition-all"></div>
            <p className="text-slate-400 text-sm font-medium flex items-center gap-2 uppercase tracking-wider mb-2">
              <TrendingUp size={16} className="text-indigo-400" /> Total Outstanding
            </p>
            <h2 className="text-3xl font-bold text-white flex items-center">
              <IndianRupee size={24} className="mr-1 text-slate-500" />
              {totalPendingAmount.toLocaleString()}
            </h2>
          </div>

          {/* This Month */}
          <div className="bg-gradient-to-br from-[#111624] to-[#1a2133] p-6 rounded-2xl border border-white/5 shadow-xl">
            <p className="text-slate-400 text-sm font-medium flex items-center gap-2 uppercase tracking-wider mb-2">
              <CalendarDays size={16} className="text-emerald-400" /> This Month
            </p>
            <h2 className="text-3xl font-bold text-white flex items-center">
              <IndianRupee size={24} className="mr-1 text-slate-500" />
              {thisMonthPending.toLocaleString()}
            </h2>
          </div>

          {/* Last Month */}
          <div className="bg-gradient-to-br from-[#111624] to-[#1a2133] p-6 rounded-2xl border border-white/5 shadow-xl">
            <p className="text-slate-400 text-sm font-medium flex items-center gap-2 uppercase tracking-wider mb-2">
              <CalendarMinus size={16} className="text-amber-400" /> Last Month
            </p>
            <h2 className="text-3xl font-bold text-white flex items-center">
              <IndianRupee size={24} className="mr-1 text-slate-500" />
              {lastMonthPending.toLocaleString()}
            </h2>
          </div>
        </div>

        {/* --- EXECUTIVE SUMMARIES --- */}
        <div>
          <h3 className="text-sm font-bold text-indigo-400 uppercase tracking-wider mb-4 pl-1 border-l-2 border-indigo-500 ml-1">
            Executive Pending Targets
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Object.entries(executiveStats).map(([name, stats]) => (
              <div key={name} className="bg-[#111624]/60 backdrop-blur-md p-4 rounded-xl border border-white/5 hover:border-indigo-500/30 transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <UserCircle2 size={18} className="text-slate-500" />
                  <span className="text-sm font-semibold text-white truncate">{name}</span>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-slate-400 flex justify-between">
                    Bills Pending: <span className="text-white font-medium">{stats.count}</span>
                  </p>
                  <p className="text-xs text-slate-400 flex justify-between items-center">
                    Amount: 
                    <span className="text-indigo-300 font-bold flex items-center">
                      <IndianRupee size={10} className="mr-0.5" />{stats.amount.toLocaleString()}
                    </span>
                  </p>
                </div>
              </div>
            ))}
            {Object.keys(executiveStats).length === 0 && !isLoading && (
              <p className="text-xs text-slate-500 italic col-span-full">No active pending bills for executives.</p>
            )}
          </div>
        </div>

        {/* --- DATA TABLE --- */}
        <div className="bg-[#111624] border border-slate-800 rounded-2xl shadow-2xl overflow-hidden">
          
          {/* Table Toolbar */}
          <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-[#0B0F19]/50">
            <h3 className="font-semibold text-white">All Records</h3>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
              <input 
                type="text" 
                placeholder="Search invoices..." 
                className="w-full pl-9 pr-4 py-1.5 bg-[#0B0F19] border border-slate-800 rounded-lg text-sm text-white placeholder-slate-500 focus:border-indigo-500 outline-none"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-400">
              <thead className="bg-[#0B0F19]/80 text-xs uppercase text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="px-6 py-4 font-medium">Customer & Shop Details</th>
                  <th className="px-6 py-4 font-medium">Exec / Invoice</th>
                  <th className="px-6 py-4 font-medium">Amount</th>
                  <th className="px-6 py-4 font-medium">Payment Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {isLoading ? (
                  <tr><td colSpan="4" className="px-6 py-8 text-center">Loading data...</td></tr>
                ) : currentTableBills.length === 0 ? (
                  <tr><td colSpan="4" className="px-6 py-8 text-center text-slate-500">No records found.</td></tr>
                ) : (
                  currentTableBills.map((bill) => (
                    <tr key={bill._id} className="hover:bg-slate-800/20 transition-colors group">
                      
                      {/* Shop & Address Info */}
                      <td className="px-6 py-4">
                        <p className="font-semibold text-white text-base">{bill.shopName}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          <span className="text-indigo-300">{bill.ownerName}</span> • {bill.whatsappNumber}
                        </p>
                        <div className="flex items-start gap-1.5 mt-1.5 text-slate-500">
                          <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                          <p 
                            className="text-xs truncate max-w-[200px] md:max-w-[300px]" 
                            title={bill.address} // Hovering shows the full address!
                          >
                            {bill.address}
                          </p>
                        </div>
                      </td>

                      {/* Exec & Invoice */}
                      <td className="px-6 py-4">
                        <p className="text-white text-xs mb-1.5 flex items-center gap-1.5">
                          <UserCircle2 size={12} className="text-slate-500" />
                          {bill.salesExecutiveName || 'N/A'}
                        </p>
                        <span className="px-2 py-0.5 bg-[#0B0F19] border border-slate-700 rounded text-[10px] font-mono text-indigo-300">
                          {bill.invoiceNumber}
                        </span>
                      </td>

                      {/* Amount */}
                      <td className="px-6 py-4">
                        <p className="font-bold text-emerald-400 flex items-center">
                          <IndianRupee size={14} className="mr-0.5 text-emerald-500/70" />
                          {Number(bill.pendingAmount).toLocaleString()}
                        </p>
                      </td>

                      {/* Interactive Status Dropdown */}
                      <td className="px-6 py-4">
                        <select 
                          value={bill.paymentStatus}
                          onChange={(e) => handleStatusChange(bill._id, e.target.value)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-bold tracking-wider outline-none cursor-pointer appearance-none border transition-all
                            ${bill.paymentStatus === 'Pending' ? 'bg-amber-500/10 text-amber-400 border-amber-500/30 focus:ring-amber-500/50' :
                              bill.paymentStatus === 'Partially Paid' ? 'bg-blue-500/10 text-blue-400 border-blue-500/30 focus:ring-blue-500/50' :
                              bill.paymentStatus === 'Completed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30 focus:ring-emerald-500/50' :
                              'bg-red-500/10 text-red-400 border-red-500/30 focus:ring-red-500/50'}`}
                        >
                          <option value="Pending" className="bg-slate-900 text-amber-400">Pending</option>
                          <option value="Partially Paid" className="bg-slate-900 text-blue-400">Partially Paid</option>
                          <option value="Overdue" className="bg-slate-900 text-red-400">Overdue</option>
                          <option value="Completed" className="bg-slate-900 text-emerald-400">Completed</option>
                        </select>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* --- PAGINATION CONTROLS --- */}
          {!isLoading && bills.length > 0 && (
            <div className="p-4 border-t border-slate-800 flex items-center justify-between bg-[#0B0F19]/50">
              <span className="text-xs text-slate-500">
                Showing <span className="text-white font-medium">{indexOfFirstItem + 1}</span> to <span className="text-white font-medium">{Math.min(indexOfLastItem, bills.length)}</span> of <span className="text-white font-medium">{bills.length}</span> entries
              </span>
              
              <div className="flex gap-2">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft size={16} />
                </button>
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default PendingBillsList;